import { useState } from 'react';

type FormKey = 'health' | 'family' | 'asset' | null;

const resources = [
  {
    key: 'health' as FormKey,
    icon: 'ri-file-search-line',
    accentColor: '#D4A373',
    tag: '最受歡迎',
    title: '保單健檢表',
    desc: '協助整理既有保單、保障範圍、繳費狀態與可能缺口，讓您一眼看清楚目前的保障全貌。',
    includes: ['現有保單清單整理', '保障範圍對照表', '繳費狀態記錄', '保障缺口初步評估'],
    formUrl: 'https://readdy.ai/api/form/d7oe9jhuelgcie28i0ug',
    btnLabel: '立即下載',
    img: 'https://readdy.ai/api/search-image?query=insurance%20policy%20checklist%20document%20form%20professional%20clean%20white%20desk%20organized%20minimal%20warm%20light&width=600&height=380&seq=dl001&orientation=landscape',
  },
  {
    key: 'family' as FormKey,
    icon: 'ri-home-heart-line',
    accentColor: '#5C5470',
    tag: '家庭必備',
    title: '家庭風險盤點表',
    desc: '從收入責任、家庭支出、醫療風險、子女照顧與父母照護面向，全面檢視家庭保障需求。',
    includes: ['家庭收入責任評估', '醫療風險盤點', '子女照顧需求分析', '父母照護費用估算'],
    formUrl: 'https://readdy.ai/api/form/d7oe9jhuelgcie28i0v0',
    btnLabel: '下載表單',
    img: 'https://readdy.ai/api/search-image?query=family%20risk%20assessment%20checklist%20document%20form%20professional%20clean%20desk%20organized%20warm%20light%20home%20planning&width=600&height=380&seq=dl002&orientation=landscape',
  },
  {
    key: 'asset' as FormKey,
    icon: 'ri-bar-chart-grouped-line',
    accentColor: '#FF8C69',
    tag: '退休必看',
    title: '資產配置與退休準備檢核表',
    desc: '協助檢視退休現金流、緊急預備金、醫療準備金與長期照護風險，讓退休規劃更有底氣。',
    includes: ['退休現金流試算', '緊急預備金評估', '醫療準備金規劃', '長期照護風險評估'],
    formUrl: 'https://readdy.ai/api/form/d7oe9jhuelgcie28i0vg',
    btnLabel: '立即下載',
    img: 'https://readdy.ai/api/search-image?query=retirement%20asset%20allocation%20checklist%20document%20form%20professional%20clean%20desk%20organized%20warm%20light%20financial%20planning&width=600&height=380&seq=dl003&orientation=landscape',
  },
];

export default function ToolsDownload() {
  const [activeForm, setActiveForm] = useState<FormKey>(null);
  const [submitted, setSubmitted] = useState<Record<string, boolean>>({});

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>, key: string, url: string) => {
    e.preventDefault();
    const form = e.currentTarget;
    const data = new URLSearchParams(new FormData(form) as unknown as Record<string, string>);
    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setSubmitted((prev) => ({ ...prev, [key]: true }));
        form.reset();
      }
    } catch {
      alert('送出失敗，請稍後再試');
    }
  };

  return (
    <section id="download-section" className="bg-white py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="text-[#D4A373] font-semibold tracking-widest mb-3" style={{ fontSize: '1rem' }}>
            FREE RESOURCES
          </p>
          <h2
            className="font-serif font-bold text-[#5C5470]"
            style={{ fontSize: 'clamp(1.5rem, 3vw, 2.125rem)', lineHeight: '1.4' }}
          >
            資源下載
          </h2>
          <div className="w-14 h-1 bg-[#D4A373] mx-auto rounded-full mt-4 mb-4"></div>
          <p className="text-gray-600 max-w-lg mx-auto" style={{ fontSize: '1.1875rem', lineHeight: '1.75' }}>
            填寫基本資料後，琮恩將以 Email 或 LINE 傳送表單給您
          </p>
        </div>

        {/* Resource Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {resources.map((r) => (
            <div
              key={r.key}
              className="bg-[#F8F5F0] rounded-3xl overflow-hidden border border-transparent hover:border-[#D4A373]/30 transition-all duration-300 flex flex-col"
            >
              {/* Image */}
              <div className="h-44 overflow-hidden flex-shrink-0">
                <img
                  src={r.img}
                  alt={r.title}
                  className="w-full h-full object-cover object-top hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* Content */}
              <div className="p-8 flex flex-col flex-1">
                <div className="flex items-center gap-3 mb-5">
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-xl flex-shrink-0"
                    style={{ backgroundColor: `${r.accentColor}18` }}
                  >
                    <i className={`${r.icon} text-xl`} style={{ color: r.accentColor }}></i>
                  </div>
                  <span
                    className="px-3 py-1 rounded-full text-white font-medium"
                    style={{ fontSize: '1rem', backgroundColor: r.accentColor }}
                  >
                    {r.tag}
                  </span>
                </div>

                <h3
                  className="font-serif font-bold text-[#5C5470] mb-3"
                  style={{ fontSize: 'clamp(1.125rem, 1.5vw, 1.375rem)', lineHeight: '1.45' }}
                >
                  {r.title}
                </h3>
                <p className="text-gray-600 mb-5" style={{ fontSize: '1.0625rem', lineHeight: '1.75' }}>
                  {r.desc}
                </p>

                {/* Includes */}
                <ul className="space-y-2 mb-6 flex-1">
                  {r.includes.map((item) => (
                    <li key={item} className="flex items-center gap-2 text-gray-600" style={{ fontSize: '1rem' }}>
                      <i className="ri-check-line flex-shrink-0" style={{ color: r.accentColor }}></i>
                      {item}
                    </li>
                  ))}
                </ul>

                {/* Download Button */}
                {activeForm !== r.key ? (
                  <button
                    onClick={() => setActiveForm(r.key)}
                    className="inline-flex items-center justify-center gap-2 text-white px-6 py-3.5 rounded-full font-semibold whitespace-nowrap transition-all duration-200 hover:scale-105 cursor-pointer"
                    style={{ fontSize: '1.0625rem', backgroundColor: r.accentColor }}
                  >
                    <i className="ri-download-line"></i>
                    {r.btnLabel}
                  </button>
                ) : submitted[r.key!] ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center">
                    <i className="ri-check-double-line text-green-500 text-2xl block mb-1"></i>
                    <p className="text-green-700 font-medium" style={{ fontSize: '1rem' }}>已送出！我們將盡快傳送給您</p>
                  </div>
                ) : (
                  <form
                    data-readdy-form
                    action={r.formUrl}
                    method="POST"
                    onSubmit={(e) => handleSubmit(e, r.key!, r.formUrl)}
                    className="space-y-3"
                  >
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="您的姓名 *"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                      style={{ fontSize: '1.0625rem' }}
                    />
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="電子郵件 *"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                      style={{ fontSize: '1.0625rem' }}
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="電話（選填）"
                      className="w-full border border-gray-200 rounded-xl px-4 py-3 text-gray-700 focus:outline-none focus:border-[#D4A373] transition-colors"
                      style={{ fontSize: '1.0625rem' }}
                    />
                    <div className="flex gap-2">
                      <button
                        type="submit"
                        className="flex-1 text-white py-3 rounded-full font-semibold whitespace-nowrap transition-all duration-200 hover:opacity-90 cursor-pointer"
                        style={{ fontSize: '1.0625rem', backgroundColor: r.accentColor }}
                      >
                        確認送出
                      </button>
                      <button
                        type="button"
                        onClick={() => setActiveForm(null)}
                        className="px-4 py-3 rounded-full border border-gray-300 text-gray-500 hover:border-gray-400 transition-colors cursor-pointer"
                        style={{ fontSize: '1.0625rem' }}
                      >
                        取消
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
